import { useEffect, useRef } from 'react'
import { prefersReducedMotion } from '../../components/reader/hooks'

/** Where the paragraph being read or recorded rests: about 35% down the viewport. */
export const FOLLOW_LINE = 0.35

/** A composer row by block id (BlockList's `data-block-id`). */
export function rowEl(blockId: string): HTMLElement | null {
  for (const el of Array.from(document.querySelectorAll<HTMLElement>('[data-block-id]'))) {
    if (el.dataset.blockId === blockId) return el
  }
  return null
}

/** Bring `blockId`'s row to the follow line, gently, unless it already sits comfortably above the dock. */
export function scrollToFollow(blockId: string, force = false): void {
  const el = rowEl(blockId)
  if (!el) return
  const r = el.getBoundingClientRect()
  const vh = window.innerHeight || 800
  const dock = document.querySelector('[data-audio-dock]')?.getBoundingClientRect().height ?? 0
  const comfortable = r.top >= vh * 0.18 && r.top <= vh * 0.52 && r.top + Math.min(r.height, 120) <= vh - dock - 16
  if (comfortable && !force) return
  try {
    window.scrollTo({ top: Math.max(0, window.scrollY + r.top - vh * FOLLOW_LINE), behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  } catch {
    /* no scrolling here (tests) */
  }
}

/**
 * While a dock mode is on, keep `targetId` near the follow line as it
 * changes. The author scrolling by hand (wheel, touch, the scrollbar) turns
 * following off through `onManual`; the dock's Follow toggle turns it back on.
 * Scrolling keys are the dock's to report (Space and ↓ mean other things there).
 */
export function useFollowScroll(targetId: string | null, active: boolean, follow: boolean, onManual: () => void): void {
  const manual = useRef(onManual)
  manual.current = onManual

  useEffect(() => {
    if (!active) return
    const off = () => manual.current()
    const onPointer = (e: PointerEvent) => {
      // A press on the page's own scrollbar.
      if (e.clientX >= document.documentElement.clientWidth && document.documentElement.clientWidth > 0) off()
    }
    window.addEventListener('wheel', off, { passive: true })
    window.addEventListener('touchmove', off, { passive: true })
    window.addEventListener('pointerdown', onPointer)
    return () => {
      window.removeEventListener('wheel', off)
      window.removeEventListener('touchmove', off)
      window.removeEventListener('pointerdown', onPointer)
    }
  }, [active])

  useEffect(() => {
    if (!active || !follow || !targetId) return
    // After layout, so a just-highlighted row is measured where it is.
    const raf = requestAnimationFrame(() => scrollToFollow(targetId))
    return () => cancelAnimationFrame(raf)
  }, [active, follow, targetId])
}
